import { useState } from 'react';
import type { ProjectSettings } from '@gc/shared';
import { ApiError } from '../../api/client.js';
import { actualizarProyecto } from '../../api/projects.js';
import { SelectorAsset } from '../biblioteca/SelectorAsset.js';

export type PanelMusicaProps = {
  readonly projectId: string;
  readonly revision: number;
  readonly status: string;
  readonly settings: ProjectSettings;
  /** Se invoca tras cada cambio: la página recarga para obtener la revisión nueva. */
  onCambio(): void;
};

/**
 * Música del proyecto.
 *
 * Es la vía para completar un vídeo cuando no hay proveedor de música
 * generativa: subir una pista propia y elegirla. Cambiarla es una edición del
 * proyecto, así que sube la revisión y hay que volver a aprobar.
 */
export function PanelMusica({
  projectId,
  revision,
  status,
  settings,
  onCambio,
}: PanelMusicaProps): React.JSX.Element {
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bloqueado = ocupado || status === 'GENERATING' || status === 'RENDERING';

  async function guardar(cambio: Partial<ProjectSettings>): Promise<void> {
    setOcupado(true);
    setError(null);
    try {
      await actualizarProyecto(projectId, { expectedRevision: revision, settings: cambio });
      onCambio();
    } catch (causa) {
      setError(causa instanceof ApiError ? causa.message : 'No se pudo guardar la música.');
    } finally {
      setOcupado(false);
    }
  }

  return (
    <section
      aria-labelledby="titulo-musica"
      style={{ border: '1px solid var(--borde)', borderRadius: '6px', padding: '1rem', margin: '1rem 0' }}
    >
      <h2 id="titulo-musica" style={{ marginTop: 0 }}>
        Música
      </h2>

      <label style={{ display: 'block' }}>
        <input
          type="checkbox"
          checked={settings.musicEnabled}
          disabled={bloqueado}
          onChange={(evento) => void guardar({ musicEnabled: evento.target.checked })}
        />{' '}
        Incluir música de fondo
      </label>

      {settings.musicEnabled && (
        <>
          <p style={{ marginBottom: '0.25rem' }}>Pista del proyecto</p>
          <SelectorAsset
            projectId={projectId}
            usage="PROJECT_MUSIC"
            seleccionado={settings.musicAssetId}
            deshabilitado={bloqueado}
            etiquetaSubida="Subir una pista propia (MP3, WAV, AAC o M4A)"
            onSeleccionar={(assetId) => void guardar({ musicAssetId: assetId })}
          />
          {settings.musicAssetId === null && (
            <p>
              <small>
                Todavía no hay pista. Sin ella el proyecto no se puede aprobar; sube una o desactiva
                la música.
              </small>
            </p>
          )}
        </>
      )}

      {error !== null && (
        <p role="alert" style={{ color: 'var(--error)' }}>
          {error}
        </p>
      )}
    </section>
  );
}
